// src/contexts/Efishery.context.ts
import { createContext } from "react";
import { Stage } from "@efishery/sdk-core";
var initialState = {
  authenticateStatus: "loading",
  auth: null,
  clientId: "",
  stage: Stage.stg,
  redirectUrl: "",
  failedRedirectUrl: "",
  profile: null,
  setProfile: () => null,
  setAuthenticateStatus: () => null
};
var AuthContext = createContext(initialState);
var Provider = AuthContext.Provider;

// src/hooks/useAuth.hook.ts
import { lazy, useCallback, useContext } from "react";

// src/constants/general.constant.ts
import { Stage as Stage2 } from "@efishery/sdk-core";
var BaseURIS = {
  [Stage2.stg]: "https://stg-account.efishery.com/",
  [Stage2.staging]: "https://stg-account.efishery.com/",
  [Stage2.uat]: "https://uat-account.efishery.com/",
  [Stage2.prod]: "https://account.efishery.com/",
  [Stage2.production]: "https://account.efishery.com/"
};

// src/libs/utils.ts
var generateQueryString = (model) => Object.entries(model).reduce(
  (prev, [key, value], index) => [...prev, `${index === 0 ? "?" : "&"}${key}=${value}`],
  []
).join("");

// src/hooks/useAuth.hook.ts
var ContentMap = {
  welcomeNotes: "welcome_notes",
  notifNotes: "notif_notes",
  footerNotes: "footer_notes"
};
var CallbackComponent = lazy(() => import("./Callback.component-3AC5BUTY.mjs"));
var useAuth = () => {
  const {
    auth,
    stage,
    authenticateStatus,
    setProfile,
    setAuthenticateStatus,
    clientId,
    redirectUrl,
    failedRedirectUrl,
    profile
  } = useContext(AuthContext);
  const isSafeAction = () => {
    if (!auth)
      throw new Error(
        "Sorry, it looks like you cannot perform this action at this time. The authentication instance has not been initiated, which is required to complete this task. Please try again once the authentication instance has been properly initialized."
      );
  };
  const exchange = useCallback(
    async (authorizationCode, grantType) => {
      setAuthenticateStatus("loading");
      try {
        const { accessToken } = await auth.persistExchange(authorizationCode, grantType);
        const profileFromSDK = await auth.getProfile(accessToken);
        setProfile(profileFromSDK.data);
        setAuthenticateStatus("authenticated");
        return profileFromSDK.data;
      } catch (err) {
        setAuthenticateStatus("rejected");
      }
    },
    [auth]
  );
  const login = useCallback(
    async (props) => {
      isSafeAction();
      const challengeCode = await auth.generateChallengeCode();
      const query = {
        code_challenge: challengeCode,
        client_id: clientId,
        redirect_url: redirectUrl,
        failed_redirect_url: failedRedirectUrl
      };
      if (props == null ? void 0 : props.content) {
        Object.entries(props == null ? void 0 : props.content).forEach(([key, value]) => {
          if (value && ContentMap[key])
            query[ContentMap[key]] = value;
        });
      }
      window.location.href = BaseURIS[stage] + generateQueryString(query);
    },
    [auth]
  );
  const refresh = useCallback(() => {
    isSafeAction();
    return auth.persistRefresh();
  }, [auth]);
  const logout = useCallback(() => {
    return auth.logout();
  }, [auth]);
  const hardRefreshProfile = useCallback(async () => {
    const profile2 = await auth.hardRefreshProfile();
    setProfile(profile2.data);
    return profile2.data;
  }, [auth]);
  return {
    authenticateStatus,
    AuthCallback: CallbackComponent,
    login,
    exchange,
    refresh,
    hardRefreshProfile,
    logout,
    profile
  };
};

export {
  AuthContext,
  Provider,
  useAuth
};
